/**
 * Shared desktop nav items — keep in sync between repos.
 *
 * MIRROR FILE. This same file is intended to live, byte-for-byte, in:
 *   - web.front.v2  → src/pages/App/components/Nav/navItems.shared.ts   (this file)
 *   - monorepo.vibedit/web → equivalent path under that repo's SideBar
 *
 * If you change one copy, copy the change to the other. The file is
 * deliberately self-contained — no React, no icon, no router, no env
 * imports — so it can be dropped into either build without friction.
 *
 * Each repo wraps these items with its own renderer that:
 *   1. Maps `iconKey` to its own icon component.
 *   2. Treats items where `home === <this app>` as internal SPA routes,
 *      and items where `home === <the other app>` as external links to
 *      that app's host.
 *   3. Layers dynamic badges (e.g. PRO based on billing) on top of the
 *      static `badge` declared here.
 */

export type NavItemId =
  | 'workspace'
  | 'search'
  | 'libraries'
  | 'podcasts'
  | 'customize'
  | 'schedule';

/** Which app owns the canonical UI for a given nav item. */
export type NavItemHome = 'starzero' | 'vibedit';

/** Static badges only. Dynamic badges (PRO from billing, etc.) are rendered locally. */
export type NavItemBadge = 'BETA' | 'NEW';

export interface SharedNavItem {
  id: NavItemId;
  /** User-facing label. */
  label: string;
  /** Logical icon identifier; each repo maps this to its own icon component. */
  iconKey: NavItemId;
  /** Which app hosts this section. */
  home: NavItemHome;
  /**
   * Path on the home app's host (always starts with `/`). The local
   * renderer joins this with its own host when the item points to
   * the other app.
   */
  path: string;
  /** Static badge rendered next to the label. */
  badge?: NavItemBadge;
}

// `as const` keeps the entries' `id`s as literal types (not widened to `NavItemId`),
// which powers the completeness check below; `satisfies` still validates each entry
// against `SharedNavItem`.
export const NAV_ITEMS = [
  {
    id: 'workspace',
    label: 'Workspace',
    iconKey: 'workspace',
    home: 'starzero',
    path: '/workspace',
  },
  {
    id: 'search',
    label: 'Search',
    iconKey: 'search',
    home: 'starzero',
    path: '/search',
  },
  {
    id: 'libraries',
    label: 'Libraries',
    iconKey: 'libraries',
    home: 'starzero',
    path: '/libraries',
  },
  {
    id: 'podcasts',
    label: 'Podcast Clipper',
    iconKey: 'podcasts',
    home: 'vibedit',
    path: '/podcasts',
    badge: 'BETA',
  },
  {
    id: 'customize',
    label: 'Customize',
    iconKey: 'customize',
    home: 'vibedit',
    path: '/customize',
  },
  // FIXME(nav-mirror): not yet copied to monorepo.vibedit (web/src/SideBar/navItems.shared.ts),
  // which also needs a `schedule` key in its own icon map. On purpose for now: this row is
  // team-only here (`FIXME(publishing-internal-only)` in stores/Debug.ts gates it in Nav.tsx),
  // and a byte-for-byte copy would show it to every vibedit user with no gate. Copy it, and
  // the icon key, when that constant flips and publishing goes public.
  {
    id: 'schedule',
    label: 'Schedule',
    iconKey: 'schedule',
    home: 'starzero',
    path: '/schedule',
  },
] as const satisfies readonly SharedNavItem[];

// Compile-time guarantee that NAV_ITEMS has an entry for every NavItemId. Adding an id
// to the union above without a matching entry makes `_MissingNavItem` a non-`never`
// type, so this assignment fails to compile — turning a would-be runtime `undefined`
// (from each app's by-id lookup) into a build error, no runtime assertion needed.
type _MissingNavItem = Exclude<NavItemId, (typeof NAV_ITEMS)[number]['id']>;
const _navItemsCoverUnion: [_MissingNavItem] extends [never] ? true : _MissingNavItem = true;
void _navItemsCoverUnion;
